import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Search, Database, Edit } from "lucide-react";

export default function AtecoPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    const { data } = await api.get("/ateco", { params: q ? { q } : {} });
    setRows(data);
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q]);

  const save = async () => {
    if (!editing?.codice) return toast.error("Codice obbligatorio");
    await api.post("/ateco", editing);
    toast.success("Salvato");
    setOpen(false); setEditing(null); load();
  };
  const del = async (codice) => {
    if (!confirm(`Eliminare ${codice}?`)) return;
    await api.delete(`/ateco/${codice}`); toast.success("Eliminato"); load();
  };

  const riskCls = (r) => ({
    basso: "bg-green-50 text-green-700 border-green-300",
    medio: "bg-orange-50 text-orange-700 border-orange-300",
    alto: "bg-red-50 text-red-700 border-red-300",
  })[r] || "bg-slate-100 border-slate-300 text-slate-700";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Amministrazione</div>
          <h1 className="font-display text-3xl font-bold text-[#0F2C59]">Tabella ATECO / Rischio</h1>
          <p className="text-slate-500">Mappa codici ATECO al livello di rischio associato.</p>
        </div>
        <Button onClick={() => { setEditing({ codice: "", descrizione: "", rischio: "basso" }); setOpen(true); }} className="bg-[#0F2C59]" data-testid="new-ateco-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo codice
        </Button>
      </div>

      <Card className="p-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca codice o descrizione…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="ateco-search" />
        </div>
        <div className="mt-3 text-xs text-slate-500 font-mono flex items-center justify-between">
          <span data-testid="ateco-total-count">
            {rows.length} {rows.length === 1 ? "codice" : "codici"}{q ? " (filtrati)" : ""}
          </span>
          {!q && rows.length > 200 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="text-[#0F2C59] hover:underline"
              data-testid="ateco-toggle-showall"
            >
              {showAll ? "Mostra solo primi 200" : `Mostra tutti (${rows.length})`}
            </button>
          )}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600 border-b border-slate-300">
            <tr>
              <th className="text-left px-4 py-2.5">Codice</th>
              <th className="text-left px-4 py-2.5">Descrizione</th>
              <th className="text-center px-4 py-2.5">Rischio</th>
              <th className="text-right px-4 py-2.5">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="4" className="text-center py-12 text-slate-400"><Database className="w-8 h-8 mx-auto mb-2 text-slate-300" />Nessun codice</td></tr>}
            {(q || showAll ? rows : rows.slice(0, 200)).map((r) => (
              <tr key={r.codice} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 font-mono font-medium">{r.codice}</td>
                <td className="px-4 py-2.5 text-slate-600">{r.descrizione}</td>
                <td className="px-4 py-2.5 text-center"><span className={`text-xs px-2 py-0.5 rounded-full border ${riskCls(r.rischio)}`}>{r.rischio}</span></td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => { setEditing(r); setOpen(true); }}
                    className="p-1.5 rounded border border-slate-200 bg-white hover:bg-blue-50 hover:border-[#0F2C59] text-[#0F2C59] transition-colors mr-1.5"
                    title="Modifica"
                    data-testid={`edit-ateco-${r.codice}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => del(r.codice)}
                    className="p-1.5 rounded border border-slate-200 bg-white hover:bg-red-50 hover:border-red-300 text-red-500 transition-colors"
                    title="Elimina"
                    data-testid={`del-ateco-${r.codice}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Codice ATECO</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Codice</Label><Input value={editing.codice} onChange={(e) => setEditing({ ...editing, codice: e.target.value })} className="mt-1.5 font-mono" data-testid="ateco-codice" /></div>
              <div><Label>Descrizione</Label><Input value={editing.descrizione} onChange={(e) => setEditing({ ...editing, descrizione: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label>Livello rischio</Label>
                <Select value={editing.rischio} onValueChange={(v) => setEditing({ ...editing, rischio: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ateco-rischio"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basso">Basso</SelectItem>
                    <SelectItem value="medio">Medio</SelectItem>
                    <SelectItem value="alto">Alto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#0F2C59]" data-testid="save-ateco-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
